import {z} from 'zod';

import i18n from '@dms/i18n';

import {
  accountingMatrixDatagridCodeObjectField,
  AccountingMatrixDatagridConfig,
  getAccountingMatrixDatagridSharedFields,
} from '../../utils/accountingMatrixDatagridConfig';

/**
 * CRUD over `accounting/v1/operation-items-record` — distinct from, and does not replace, the
 * existing read-only `getOperationItems` query (`accounting/v1/operation-items`) in
 * `metadaAccounting/api.ts`, which serves a different purpose. See T20-137887.
 *
 * `application`/`itemGroup` aren't in the swagger's `required` list, but `applicationId` is a
 * NOT NULL column on `ACC_ItemCodeService` that BE doesn't validate at the API layer —
 * live-tested, an omitted value 500s with a raw Postgres constraint error instead of a clean
 * mandatory-field message (same pattern already seen on M205, T20-137885). Both required here
 * as a precaution; relax `itemGroup` back to optional if BE confirms it's actually nullable.
 */
export const getAccountingMatrixDatagridOperationItemsConfig =
  (): AccountingMatrixDatagridConfig => ({
    dgCode: 'operation-items',
    fields: [
      {
        name: 'operationId',
        label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldOperationId'),
        type: 'text',
        isRequired: true,
      },
      {
        name: 'operationItemName',
        label: i18n.t('general.labels.name'),
        type: 'text',
        isRequired: true,
      },
      {
        name: 'itemCode',
        label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldItemCode'),
        type: 'text',
        isRequired: true,
      },
      {
        name: 'application',
        label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldApplication'),
        type: 'choice',
        codelistCategory: 'ACCapplicationModule',
        isRequired: true,
        ...accountingMatrixDatagridCodeObjectField('application'),
      },
      {
        name: 'itemGroup',
        label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldItemGroup'),
        type: 'choice',
        codelistCategory: 'ACCitemGroups',
        isRequired: true,
        ...accountingMatrixDatagridCodeObjectField('itemGroup'),
      },
      ...getAccountingMatrixDatagridSharedFields(),
    ],
    schema: z.object({
      operationId: z.string().min(1),
      operationItemName: z.string().min(1),
      itemCode: z.string().min(1),
      application: z.string().min(1),
      itemGroup: z.string().min(1),
      billingInformation: z.string().min(1),
      validFrom: z.string(),
      validTo: z.string(),
    }),
  });
